
import React, { FC, useEffect, useRef, useState } from "react";
import axios from "axios";
import DOMPurify from "dompurify";
import { Alert, Snackbar } from "@mui/material";
import { base_url } from "constant/constant";
import { useLocale } from "i18n/locale";
import ContactFormModal from "shared/Footer/ContactForm";
import iconCompliance from "images/legal/icon-compliance.svg";
import iconHousing from "images/legal/icon-housing.svg";
import iconManagement from "images/legal/icon-management.svg";
import iconCommunity from "images/legal/icon-community.svg";
import iconTechnology from "images/legal/icon-technology.svg";
import parisIconLarge from "images/legal/paris-icon-large.svg";
import brusselsIcon from "images/header/brussels-icon.svg";
import accordionOpen from "images/legal/accordion-open.svg";
import accordionClosed from "images/legal/accordion-closed.svg";
import ctaIcon from "images/legal/cta-icon.svg";
import ctaBg from "images/legal/cta-bg.png";
import techReservations from "images/legal/tech/reservations.svg";
import techOwnerReporting from "images/legal/tech/owner-reporting.svg";
import techMaintenance from "images/legal/tech/maintenance.svg";
import techCompliance from "images/legal/tech/compliance.svg";
import techPricing from "images/legal/tech/pricing.svg";
import techAccounting from "images/legal/tech/accounting.svg";
import techGuestComms from "images/legal/tech/guest-comms.svg";
import techDistribution from "images/legal/tech/distribution.svg";

// `key`   -> stable CMS field id (tabs.<key>); editing the label never breaks nav.
// `target`-> the fixed string scrollToSection() understands (section to scroll to).
// `label` -> hardcoded fallback shown until CMS content loads / if it's missing.
const TABS = [
  { key: "responsible", target: "Responsible hospitality", label: "Responsible hospitality" },
  { key: "paris", target: "Paris Region", label: "Paris Region" },
  { key: "brussels", target: "Brussels Region", label: "Brussels Region" },
  { key: "technology", target: "Technology", label: "Technology" },
  // { key: "faqs", target: "FAQs", label: "FAQs" },
];

const DEFAULT_STATS = [
  { number: "90", desc: "nights per year for primary\nresidences in the Paris Region" },
  { number: "120", desc: "nights per year for primary\nresidences in the Brussels Region" },
  { number: "1+", desc: "month in the Paris\nRegion for mid-term rentals" },
  { number: "3+", desc: "months in the Brussels\nRegion for mid-term rentals" },
];

const PILLS = [
  { icon: iconCompliance, label: "Compliance first", iconW: 16, iconH: 12 },
  { icon: iconHousing, label: "Housing protection", iconW: 14, iconH: 14 },
  { icon: iconManagement, label: "Professional management", iconW: 11, iconH: 13 },
  { icon: iconCommunity, label: "Local community balance", iconW: 15, iconH: 14 },
  { icon: iconTechnology, label: "Technology-driven operations", iconW: 16, iconH: 14 },
];

const PARIS_PRIMARY_FAQS = [
  {
    question: "What happens when the annual quota is reached?",
    answer: "Once the annual quota has been reached, the property is automatically blocked from further short-term rentals until the following calendar year. The counter resets to zero on January 1st.\n\nFor full transparency, the remaining quota is displayed on each property's page on our website.",
  },
  { question: "Can I obtain an exemption to rent above the annual quota?",
    answer: "In limited circumstances, yes. If exceptional professional, medical, or family reasons temporarily prevent you from occupying your primary residence, the City of Paris may consider an exemption. Such requests are reviewed on a case-by-case basis and are not granted automatically. If you believe you may qualify, our team can help you assess your situation and guide you through the applicable administrative procedures. "
  },
  {
    question: "Can I use my full annual short-term rental quota and then switch to a few mid-term rental leases? ",
    answer: "No, we do not combine these rental models. A property is managed either as a primary residence, in which case short-term rentals are permitted until the annual quota is reached and the property is then blocked for the remainder of the calendar year, or as a secondary residence, in which case only mid-term or long-term rentals are offered. This approach ensures compliance with the applicable legal framework. " 
  },
  { question: "I'm not the owner, but I want to rent out my primary residence while I'm away. Is it possible?",
    answer: "As a tenant, you may be able to sublet your primary residence, provided you obtain the prior written consent of your landlord and comply with allapplicable French regulations. Before listing your property, we will review your situation to ensure that the necessary authorisations are in place and thatthe rental complies with the applicable legal framework. This helps protect both you and the property owner while ensuring full compliance with local regulations." 
  },
  { question: "I only go away in August and would like to rent out my property. Do you accept? ",
    answer: "Yes, we do. However, please note that August has become one of the weakest months of the year in the Paris Region, comparable to January. Supply now significantly exceeds demand, making it a relatively slow period for short-term rentals. We will be happy to assess your property's potential and provide realistic revenue expectations based on current market conditions. " 
  },
  { question: "Should a management company hold a Carte G and comply with the Hoguet Law?",
    answer: `Yes. In France, property management companies or Conciergerie should hold a Carte G and comply with the Hoguet Law. This ensures that owners' rental income is held in dedicated bank accounts, separate from the company's funds, and is protected by a financial guarantee like SOCAF and professional liability insurance. At Europea Residences Group, our French entity, RENT EURL, holds a Carte G, benefits from a SOCAF financial guarantee, and complies with all obligations of the Hoguet Law.\n 
             In Belgium, our property management activities are registered with the IPI (Institut Professionnel des Agents Immobiliers) and covered by mandatory professional liability insurance, providing the same level of professionalism and owner protection. Rental income is also held in dedicated bank accounts, separate from the company's own funds. `
  },
  { question: "What kind of technology does Europea use?",
    answer: "We use only proprietary technology developed entirely in-house. Every three years, we rebuild our Management platforms and apps from the ground up using the latest web and mobile technologies to provide the best possible experience for our property owners, partners, and team."
  },
];

const PARIS_SECONDARY_FAQS = [
  { question: "Can the mid-term rental model work throughout the entire year? ",
    answer: "Yes, but financial return should not be the primary motivation. Mid-term rentals have become an increasingly competitive market, driven by the growing mobility of professionals, students, researchers, and expatriates across Europe. Their greatest advantage is the flexibility they offer property owners: the freedom to use the property whenever needed, flexible rental periods without long-term commitments, the opportunity to generate income while covering ownership costs, and regular professional cleaning and inspections that help preserve the property and identify maintenance issues before they become costly repairs. For many owners, this balance between flexibility, property preservation, and recurring income is the true value of the mid-term rental model. "
  },
  { question: "What kind of tenants stay for one month or longer? ",
    answer: "Our typical tenants are between 40 and 65 years old, with a majority of women relocating to Paris for professional assignments, temporary work, studies, or research. Approximately 50% of our tenants are French or Belge citizens, while 75% are European citizens. Our selection process focuses on responsible tenants seeking a comfortable and fully furnished home for a temporary residential stay. "
  },
  {
    question: "Are there any requirements for tenants staying in Paris for one month or longer?",
    answer: "Yes. Depending on the type of rental agreement, tenants staying from 1 to 3 months may be required to complete a declaration on honour confirming that they meet the legal requirements for a mid-term rental. In Paris, mid-term rentals are intended for eligible residential stays, such as professional assignments, work missions, studies, training, or temporary relocation.. Please note that tourist stays are not permitted under this rental model. For stays of more than 3 months, no specific eligibility requirements apply. "
  }
];

const PARIS_HOTEL_FAQS = [
  {
    question: "Can you manage all types of properties, including hotels, apart-hotels, tourist residences, and bed & breakfasts?",
    answer: "Flexible living is our core activity, and our team comes from a strong hospitality background. We design, develop, and fully manage hotels, apart-hotels, tourist residences, and bed & breakfasts in Paris Regions and Brussels Region. Feel free to contact us to discuss your project."
  }
];

const BRUSSELS_PRIMARY_FAQS = [
  {
    question: "What happens when the annual quota is reached?",
    answer: "Once the annual quota has been reached, the property is automatically blocked from further short-term rentals until the following calendar year.",
  },
  { question: "Can I obtain an exemption to rent above the annual quota?",
    answer: "We are not aware of any possibility granted by the Brussels administration to book your primary residence above the legal quotas of 120 nights."
   },
  { question: "I own a studio apartment. Do you accept small properties?",
    answer: "At this time, we do manage studio apartments with a floor area of 35 m² +."
  },
  { question: "What kind of technology do you use?",
    answer: "We use only proprietary technology developed entirely in-house. Every three years, we rebuild our Management platforms and apps from the ground up using the latest web and mobile technologies to provide the best possible experience for our property owners, partners, and team."
  },
];

const BRUSSELS_SECONDARY_FAQS = [
  { question: "Can the mid-term rental model work in Brussels throughout the entire year?",
    answer: "Yes. Mid-term rentals in the Brussels Region have become an important and growing market, driven by the mobility of professionals working for EU and NATO institutions, as well as students, researchers, expatriates, and international professionals. Their greatest advantage is the flexibility they offer property owners: the freedom to use the property whenever needed, flexible rental periods without long-term commitments, the opportunity to generate income while covering ownership costs, and regular professional cleaning and inspections that help preserve the property and identify maintenance issues before they become costly repairs. For many owners, this balance between flexibility, property preservation, and recurring income is the true value of the mid-term rental model."
  },
  // { question: "Is your management commission the same for short-, mid-, and long-term rentals?" },
  // { question: "Does your activity comply with mid- and long-term rental legislation?" },
  // { question: "What are the benefits of using your company for mid- and long-term rentals?" },
  // { question: "Can short-term rentals be accepted between long-term tenants?" },
];

const BRUSSELS_HOTEL_FAQS = [
  { question: "How likely is a project to obtain the necessary licenses?",
    answer: "Residential properties cannot generally be converted into apart-hotels or tourist residences. Urban planning authorities almost never approve such changes of use, except in exceptional large-scale projects that provide significant public benefits, such as childcare facilities or other community services.\n\nCommercial properties, however, may be converted into hotel or hospitality units, provided the project is well designed and aligns with the objectives of the local municipality and the urban planning authorities. In some areas of Brussels, certain streets contain a high number of vacant office buildings, resulting in limited economic activity and reduced tax revenue. Where a project can demonstrate that it will contribute to the revitalization of the neighborhood, the administration may view the conversion favorably.\n\nEvery application is assessed on a case-by-case basis. Obtaining the necessary permits requires careful preparation, a strong business case, and close collaboration with the Brussels Regional Administration and the relevant municipal authorities throughout the process."
  },
  { question: "How can you support a hospitality project?",
    answer: (
      <>
        {"Our team has extensive experience with hospitality property conversions and a thorough understanding of the regulatory and urban planning requirements in Belgium. We can accurately assess whether your project has a realistic chance of obtaining the necessary approvals from the relevant administrative and urban planning authorities.\n\nIf the project is deemed viable, we can manage the entire transformation process in collaboration with experienced architects, guiding you from the initial feasibility study through to the final approvals.\n\nPlease note that the permitting and transformation process typically takes between "}
        <strong>6 months and 2 years</strong>
        {", depending on the complexity, size, and specific characteristics of the project."}
      </>
    )
  },
];

const TECH_CARDS_LEFT = [
  { icon: techReservations, label: "Reservations", iconW: 14, iconH: 15 },
  { icon: techOwnerReporting, label: "Owner reporting", iconW: 14, iconH: 16 },
  { icon: techMaintenance, label: "Maintenance", iconW: 18, iconH: 16 },
  { icon: techCompliance, label: "Compliance monitoring", iconW: 16, iconH: 16 },
];

const TECH_CARDS_RIGHT = [
  { icon: techPricing, label: "Pricing", iconW: 10, iconH: 16 },
  { icon: techAccounting, label: "Accounting", iconW: 12, iconH: 16 },
  { icon: techGuestComms, label: "Guest communications", iconW: 16, iconH: 16 },
  { icon: techDistribution, label: "Multi-channel", iconW: 17, iconH: 17 },
];

// CMS-editable copy comes back as { faq_0: { question, answer }, faq_1: {...}, ... }
// (that's the shape the generic CMS text editor needs) — turn it into an
// ordered array for rendering, same shape as the PARIS_*_FAQS / BRUSSELS_*_FAQS
// fallbacks above.
type Faq = { question: string; answer: React.ReactNode };
// Body copy for the legal page can be edited as rich text in the CMS (bold /
// italic / lists / links), which arrives as an HTML string. Render it through
// DOMPurify so only safe formatting tags survive. Plain-text values (existing
// content that was never formatted) and the hardcoded fallbacks render as-is.
const RICH_CFG = {
  ALLOWED_TAGS: ["b", "strong", "i", "em", "u", "a", "br", "p", "ul", "ol", "li", "span"],
  ALLOWED_ATTR: ["href", "target", "rel"],
};
const looksLikeHtml = (s: string) => /<[a-z][\s\S]*?>/i.test(s);

const RichText: FC<{
  value?: string;
  fallback?: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
}> = ({ value, fallback, className, style }) => {
  const cls = ["legal-rich", className].filter(Boolean).join(" ");
  if (typeof value === "string" && value.trim() !== "") {
    if (looksLikeHtml(value)) {
      return (
        <div
          className={cls}
          style={{ ...style, whiteSpace: "normal" }}
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(value, RICH_CFG) }}
        />
      );
    }
    // Plain-text CMS value: the caller's style already has whiteSpace: pre-line.
    return (
      <div className={cls} style={style}>
        {value}
      </div>
    );
  }
  return (
    <div className={cls} style={style}>
      {fallback}
    </div>
  );
};

const toFaqArray = (group: any): Faq[] => {
  if (!group || typeof group !== "object") return [];
  return Object.keys(group)
    .filter((k) => k.startsWith("faq_"))
    .sort((a, b) => Number(a.split("_")[1]) - Number(b.split("_")[1]))
    .map((k) => group[k])
    .filter((f) => f && f.question);
};

const LegalPageLayout: FC = () => {
  // Which FAQ answers are expanded. Tracked by POSITION (sectionKey-index), not
  // by question text — so reordering questions in the CMS keeps the *first*
  // question of a section open, instead of following a specific question around.
  // Defaults: first question of Paris primary ("pp-0") and Brussels primary ("bp-0").
  const [openFaqs, setOpenFaqs] = useState<Set<string>>(new Set(["pp-0", "bp-0"]));
  // Accordion behaviour: opening a question closes any other open one (only one
  // open at a time). Clicking an already-open question collapses it.
  const toggleFaq = (id: string) =>
    setOpenFaqs((prev) => (prev.has(id) ? new Set() : new Set([id])));
  // Site-wide language choice (EN / FR) from src/i18n/locale.ts, driven by the
  // header/footer switcher. Aliased to lang and used to pick CMS text.
  const { locale: lang } = useLocale();

  // CMS content (europeawebcms "Legal Page" section). Falls back to the
  // hardcoded copy above until it loads (or if it's ever missing), so the
  // page never renders blank.
  const [cms, setCms] = useState<any>(null);
  useEffect(() => {
    axios
      .get(`${base_url}/website/get_by_name`, { params: { name: "Legal Page" } })
      .then((res) => {
        if (!res.data?.error && res.data?.data) setCms(res.data.data);
      })
      .catch(() => {
        // CMS unreachable — keep showing the hardcoded fallback copy below.
      });
  }, []);
  const cmsText = lang === "fr" ? cms?.french_text : cms?.text;
  const cmsImages = cms?.images;

  const parisPrimaryFaqs = cmsText?.paris?.primary ? toFaqArray(cmsText.paris.primary) : PARIS_PRIMARY_FAQS;
  const parisSecondaryFaqs = cmsText?.paris?.secondary ? toFaqArray(cmsText.paris.secondary) : PARIS_SECONDARY_FAQS;
  const parisHotelFaqs = cmsText?.paris?.hotel ? toFaqArray(cmsText.paris.hotel) : PARIS_HOTEL_FAQS;
  const brusselsPrimaryFaqs = cmsText?.brussels?.primary ? toFaqArray(cmsText.brussels.primary) : BRUSSELS_PRIMARY_FAQS;
  const brusselsSecondaryFaqs = cmsText?.brussels?.secondary ? toFaqArray(cmsText.brussels.secondary) : BRUSSELS_SECONDARY_FAQS;
  const brusselsHotelFaqs = cmsText?.brussels?.hotel ? toFaqArray(cmsText.brussels.hotel) : BRUSSELS_HOTEL_FAQS;
  // Sub-header nav tabs: labels are CMS-editable (tabs.responsible/paris/...),
  // while `target` stays fixed so scroll navigation keeps working.
  const legalTabs = TABS.map((t) => ({ ...t, label: cmsText?.tabs?.[t.key] || t.label }));
  // "Our View" pills: labels are CMS-editable (pills.pill_1..pill_5), icons stay
  // fixed design assets (mapped by position).
  const legalPills = PILLS.map((p, i) => ({ ...p, label: cmsText?.pills?.[`pill_${i + 1}`] || p.label }));
  // Technology cards: labels are CMS-editable (card_1..card_8), icons stay fixed
  // design assets. Left column = card_1..4, right column = card_5..8.
  const techCardsLeft = TECH_CARDS_LEFT.map((c, i) => ({ ...c, label: cmsText?.technology?.[`card_${i + 1}`] || c.label }));
  const techCardsRight = TECH_CARDS_RIGHT.map((c, i) => ({ ...c, label: cmsText?.technology?.[`card_${i + 5}`] || c.label }));
  const parisRef = useRef<HTMLDivElement>(null);
  const brusselsRef = useRef<HTMLDivElement>(null);
  const technologyRef = useRef<HTMLDivElement>(null);
  const faqsRef = useRef<HTMLDivElement>(null);

  // Contact form modal (shared site-wide form) triggered by the CTA button.
  const [contactFormValues, setContactFormValues] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
    isChecked: false,
  });
  const [contactOpen, setContactOpen] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">("success");
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const handleContactClose = () => {
    setContactOpen(false);
    setContactFormValues({ name: "", email: "", phone: "", message: "", isChecked: false });
  };

  const scrollToSection = (tab: string) => {
    if (tab === "Responsible hospitality") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else if (tab === "Paris Region" && parisRef.current) {
      parisRef.current.scrollIntoView({ behavior: "smooth" });
    } else if (tab === "Brussels Region" && brusselsRef.current) {
      brusselsRef.current.scrollIntoView({ behavior: "smooth" });
    } else if (tab === "Technology" && technologyRef.current) {
      technologyRef.current.scrollIntoView({ behavior: "smooth" });
    } else if (tab === "FAQs" && faqsRef.current) {
      faqsRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <div className="legal-justify">
      {/* Justify body copy (paragraphs + FAQ answers). Inline text-align:center
          on the quote/CTA/labels still wins, so those stay centered. */}
      <style>{`
        .legal-justify p,
        .legal-justify .faq-answer { text-align: justify; }
        /* Rich-text (HTML from the CMS) rendered inside .legal-rich */
        .legal-rich { text-align: justify; }
        .legal-rich p { margin: 0 0 16px; }
        .legal-rich p:last-child { margin-bottom: 0; }
        .legal-rich ul, .legal-rich ol { margin: 0 0 16px; padding-left: 26px; list-style-position: outside; }
        .legal-rich ul { list-style-type: disc; }
        .legal-rich ol { list-style-type: decimal; }
        .legal-rich li { margin: 0 0 4px; display: list-item; }
        .legal-rich strong, .legal-rich b { font-weight: 600; color: #01152E; }
        .legal-rich a { color: #B88333; text-decoration: underline; }
        /* CTA copy sits on the dark card: centered, white, no paragraph gaps */
        .legal-rich.cta-rich { text-align: center; }
        .legal-rich.cta-rich p { margin: 0; text-align: center; }
        .legal-rich.cta-rich strong, .legal-rich.cta-rich b { color: inherit; }
      `}</style>
      {/* Teal sub-header */}
      <div
        className="w-full flex items-center px-4 sm:px-8 lg:px-[120px] py-8 sm:py-10 xl:py-0 xl:min-h-[175px]"
        style={{ backgroundColor: "#01152E", position: "relative" }}
      >
        <div className="flex flex-col xl:flex-row xl:items-start gap-3 xl:gap-[187px] w-full">
          <h1
            className="text-[32px] sm:text-[40px] lg:text-[48px] leading-[38px] sm:leading-[48px] lg:leading-[56px] w-full xl:basis-[445px] xl:shrink xl:min-w-0"
            style={{
              fontFamily: "Poppins, sans-serif",
              fontWeight: 600,
              color: "#ffffff",
              margin: 0,
            }}
          >
            {cmsText?.header?.title || "Legal Framework"}
          </h1>
          <p
            className="text-[16px] leading-[24px] w-full xl:flex-1 xl:min-w-0"
            style={{
              fontFamily: "Roboto, sans-serif",
              fontWeight: 400,
              color: "#ffffff",
              margin: 0,
              whiteSpace: "pre-line",
            }}
          >
            {cmsText?.header?.subtitle ||
              "A responsible approach to short- and mid-term accommodation in Paris and Brussels: compliant, transparent, community-friendly, and adapted to the realities of modern mobility."}
          </p>
        </div>
      </div>

      {/* Cream menu bar — sticky below header */}
      <div
        className="w-full flex items-center px-4 sm:px-8 lg:px-[120px] h-[60px] sm:h-[70px] lg:h-[80px] overflow-x-auto"
        style={{
          backgroundColor: "#FAF7F1",
          position: "sticky",
          top: 76,
          zIndex: 30,
        }}
      >
        <div className="flex items-center gap-[20px]">
          {legalTabs.map((tab, index) => (
            <React.Fragment key={tab.key}>
              {index > 0 && (
                <div
                  style={{
                    width: 1,
                    height: 12,
                    backgroundColor: "#D6D6D6",
                    flexShrink: 0,
                  }}
                />
              )}
              <span
                onClick={() => scrollToSection(tab.target)}
                className="text-[13px] lg:text-[14px] leading-[24px] whitespace-nowrap"
                style={{
                  fontFamily: "Poppins, sans-serif",
                  fontWeight: 600,
                  color: "#01152E",
                  textTransform: "uppercase",
                  letterSpacing: "1.4px",
                  cursor: "pointer",
                  flexShrink: 0,
                }}
              >
                {tab.label}
              </span>
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Responsible hospitality section */}
      <div
        className="w-full flex flex-col xl:flex-row xl:items-start px-4 sm:px-8 lg:px-[120px] py-10 sm:py-16 lg:py-[90px] gap-8 xl:gap-[142px]"
        style={{ backgroundColor: "#ffffff" }}
      >
        {/* Left column */}
        <div className="flex flex-col gap-3 w-full xl:basis-[498px] xl:shrink xl:min-w-0">
          <span
            style={{
              fontFamily: "Poppins, sans-serif",
              fontWeight: 600,
              fontSize: 16,
              lineHeight: "24px",
              color: "#B88333",
              textTransform: "uppercase",
              letterSpacing: "3.2px",
            }}
          >
            {cmsText?.ourView?.label || "Our View"}
          </span>
          <h2
            className="text-[26px] sm:text-[32px] lg:text-[36px] leading-[32px] sm:leading-[42px] lg:leading-[46px]"
            style={{
              fontFamily: "Poppins, sans-serif",
              fontWeight: 600,
              color: "#01152E",
              letterSpacing: "0.72px",
              margin: 0,
            }}
          >
            {cmsText?.ourView?.heading || "Responsible hospitality, by design"}
          </h2>
        </div>

        {/* Right column */}
        <div className="flex flex-col gap-[30px] w-full xl:flex-1 xl:min-w-0">
          <RichText
            value={cmsText?.ourView?.paragraph}
            style={{
              fontFamily: "Roboto, sans-serif",
              fontWeight: 400,
              fontSize: 16,
              lineHeight: "24px",
              color: "#445162",
              margin: 0,
              whiteSpace: "pre-line",
            }}
            fallback={
              <>
                Without proper regulation, short-term rentals can negatively impact
                the availability of housing for long-term residents. We believe we
                all share a{" "}
                <span
                  style={{
                    fontWeight: 600,
                    color: "#01152E",
                  }}
                >
                  social responsibility
                </span>{" "}
                to contribute to a fair balance between long-term housing and
                alternative accommodation solutions.
                {"\n\n"}
                We also believe short- and mid-term rentals benefit society by
                creating{" "}
                <span
                  style={{
                    fontWeight: 600,
                    color: "#01152E",
                  }}
                >
                  employment, economic activity, and tax revenues
                </span>{" "}
                from properties that might otherwise remain vacant.
              </>
            }
          />

          {/* Pill tags */}
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "10px 6px",
              alignItems: "center",
            }}
          >
            {legalPills.map((pill, idx) => (
              <div
                key={idx}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 10,
                  height: 30,
                  padding: "0 12px",
                  backgroundColor: "#FAF7F1",
                  border: "1px solid #D6D6D6",
                  borderRadius: 20,
                }}
              >
                <img
                  src={pill.icon}
                  alt=""
                  style={{ width: pill.iconW, height: pill.iconH }}
                />
                <span
                  style={{
                    fontFamily: "Poppins, sans-serif",
                    fontWeight: 500,
                    fontSize: 12,
                    lineHeight: "24px",
                    color: "#01152E",
                    whiteSpace: "nowrap",
                  }}
                >
                  {pill.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Stats bar */}
      <div
        className="w-full flex justify-center px-4 sm:px-8 lg:px-[120px] py-8 sm:py-12 lg:py-[60px]"
        style={{ backgroundColor: "#F5EEE4" }}
      >
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 sm:gap-x-8 lg:gap-x-[33px] gap-y-8">
          {DEFAULT_STATS.map((defaultStat, idx) => {
            const n = idx + 1;
            const stat = {
              number: cmsText?.stats?.[`stat${n}_number`] || defaultStat.number,
              desc: cmsText?.stats?.[`stat${n}_desc`] || defaultStat.desc,
            };
            return (
            <div
              key={idx}
              className="flex flex-col items-center gap-[14px]"
            >
              <span
                className="text-[40px] sm:text-[50px] lg:text-[60px] leading-[44px] sm:leading-[54px] lg:leading-[60px]"
                style={{
                  fontFamily: "Poppins, sans-serif",
                  fontWeight: 600,
                  color: "#B88333",
                  textAlign: "center",
                }}
              >
                {stat.number}
              </span>
              <span
                style={{
                  fontFamily: "Poppins, sans-serif",
                  fontWeight: 500,
                  fontSize: 16,
                  lineHeight: "24px",
                  color: "#01152E",
                  textAlign: "center",
                  letterSpacing: "0.32px",
                  whiteSpace: "pre-line",
                }}
              >
                {stat.desc}
              </span>
            </div>
            );
          })}
        </div>
      </div>

      {/* Paris Region section */}
      <div
        ref={parisRef}
        className="w-full flex flex-col xl:flex-row xl:items-start px-4 sm:px-8 lg:px-[120px] py-10 sm:py-16 lg:py-[90px] gap-8 xl:gap-[142px]"
        style={{ backgroundColor: "#ffffff", scrollMarginTop: 156 }}
      >
        {/* Left column */}
        <div className="flex flex-col gap-[30px] w-full xl:basis-[498px] xl:shrink xl:min-w-0">
          <div style={{ display: "flex", flexDirection: "column", gap: 21 }}>
            <img
              src={parisIconLarge}
              alt="Paris"
              style={{ width: 53, height: 53 }}
            />
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <span
                style={{
                  fontFamily: "Poppins, sans-serif",
                  fontWeight: 600,
                  fontSize: 16,
                  lineHeight: "24px",
                  color: "#B88333",
                  textTransform: "uppercase",
                  letterSpacing: "3.2px",
                }}
              >
                {cmsText?.paris?.country || "FRANCE"}
              </span>
              <h2
                className="text-[26px] sm:text-[32px] lg:text-[36px] leading-[34px] sm:leading-[44px] lg:leading-[50px]"
                style={{
                  fontFamily: "Poppins, sans-serif",
                  fontWeight: 600,
                  color: "#01152E",
                  letterSpacing: "0.72px",
                  margin: 0,
                }}
              >
                {cmsText?.paris?.city || "Paris"}
              </h2>
            </div>
          </div>
          <RichText
            value={cmsText?.paris?.intro}
            style={{
              fontFamily: "Roboto, sans-serif",
              fontWeight: 400,
              fontSize: 16,
              lineHeight: "24px",
              color: "#445162",
              margin: 0,
              whiteSpace: "pre-line",
            }}
            fallback="In France, the ELAN Law and the Le Meur Law (December 2024) establish the legal framework for Flexible living. As housing conditions vary from one municipality to another, these laws also allow local authorities to introduce additional restrictions where necessary to maintain a balanced and sustainable housing market."
          />
        </div>

        {/* Right column - Accordions */}
        <div className="flex flex-col gap-[60px] w-full xl:flex-1 xl:min-w-0">
          {/* Primary Residences */}
          <div style={{ display: "flex", flexDirection: "column", gap: 36 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
              <h3
                style={{
                  fontFamily: "Poppins, sans-serif",
                  fontWeight: 600,
                  fontSize: 28,
                  lineHeight: "36px",
                  color: "#01152E",
                  margin: 0,
                }}
              >
                {cmsText?.paris?.primary?.title || "Primary Residences"}
              </h3>
              <RichText
                value={cmsText?.paris?.primary?.description}
                style={{
                  fontFamily: "Roboto, sans-serif",
                  fontWeight: 400,
                  fontSize: 16,
                  lineHeight: "24px",
                  color: "#445162",
                  margin: 0,
                  whiteSpace: "pre-line",
                }}
                fallback="You live in your apartment most of the year, but spend several weeks or months elsewhere for professional or family reasons."
              />
            </div>
            <div style={{ display: "flex", flexDirection: "column" }}>
              {parisPrimaryFaqs.map((faq, i) => {
                const faqId = "pp-" + i;
                const isOpen = openFaqs.has(faqId);
                return (
                  <div key={faqId}>
                    <div style={{ width: "100%", height: 1, backgroundColor: "#D6D6D6" }} />
                    <div
                      onClick={() => toggleFaq(faqId)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 21,
                        padding: "20px 0",
                        cursor: "pointer",
                      }}
                    >
                      <span
                        className="flex-1 min-w-0"
                        style={{
                          fontFamily: "Poppins, sans-serif",
                          fontWeight: 500,
                          fontSize: 16,
                          lineHeight: "24px",
                          color: "#01152E",
                          letterSpacing: "0.32px",
                        }}
                      >
                        {faq.question}
                      </span>
                      <img
                        src={isOpen ? accordionOpen : accordionClosed}
                        alt=""
                        style={{
                          width: 28,
                          height: 28,
                          flexShrink: 0,
                          transition: "transform 300ms ease",
                          transform: isOpen ? "rotate(0deg)" : "rotate(0deg)",
                        }}
                      />
                    </div>
                    {faq.answer && (
                      <div
                        style={{
                          display: "grid",
                          gridTemplateRows: isOpen ? "1fr" : "0fr",
                          transition: "grid-template-rows 300ms ease",
                        }}
                      >
                        <div style={{ overflow: "hidden", minHeight: 0 }}>
                          <RichText
                            className="faq-answer"
                            value={typeof faq.answer === "string" ? faq.answer : undefined}
                            fallback={faq.answer}
                            style={{
                              fontFamily: "Roboto, sans-serif",
                              fontWeight: 400,
                              fontSize: 16,
                              lineHeight: "24px",
                              color: "#445162",
                              paddingBottom: 20,
                              whiteSpace: "pre-line",
                            }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
              <div style={{ width: "100%", height: 1, backgroundColor: "#D6D6D6" }} />
            </div>
          </div>

          {/* Secondary Residences */}
          <div style={{ display: "flex", flexDirection: "column", gap: 36 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
              <h3
                style={{
                  fontFamily: "Poppins, sans-serif",
                  fontWeight: 600,
                  fontSize: 28,
                  lineHeight: "36px",
                  color: "#01152E",
                  margin: 0,
                }}
              >
                {cmsText?.paris?.secondary?.title || "Secondary Residences"}
              </h3>
              <RichText
                value={cmsText?.paris?.secondary?.description}
                style={{
                  fontFamily: "Roboto, sans-serif",
                  fontWeight: 400,
                  fontSize: 16,
                  lineHeight: "24px",
                  color: "#445162",
                  margin: 0,
                  whiteSpace: "pre-line",
                }}
                fallback={"You own a property in the Paris Region but only use it a few months each year."}
              />
            </div>
            <div style={{ display: "flex", flexDirection: "column" }}>
              {parisSecondaryFaqs.map((faq, i) => {
                const faqId = "ps-" + i;
                const isOpen = openFaqs.has(faqId);
                return (
                  <div key={faqId}>
                    <div style={{ width: "100%", height: 1, backgroundColor: "#D6D6D6" }} />
                    <div
                      onClick={() => toggleFaq(faqId)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 21,
                        padding: "20px 0",
                        cursor: "pointer",
                      }}
                    >
                      <span
                        className="flex-1 min-w-0"
                        style={{
                          fontFamily: "Poppins, sans-serif",
                          fontWeight: 500,
                          fontSize: 16,
                          lineHeight: "24px",
                          color: "#01152E",
                          letterSpacing: "0.32px",
                        }}
                      >
                        {faq.question}
                      </span>
                      <img
                        src={isOpen ? accordionOpen : accordionClosed}
                        alt=""
                        style={{
                          width: 28,
                          height: 28,
                          flexShrink: 0,
                          transition: "transform 300ms ease",
                        }}
                      />
                    </div>
                    {(faq as any).answer && (
                      <div
                        style={{
                          display: "grid",
                          gridTemplateRows: isOpen ? "1fr" : "0fr",
                          transition: "grid-template-rows 300ms ease",
                        }}
                      >
                        <div style={{ overflow: "hidden", minHeight: 0 }}>
                          <RichText
                            className="faq-answer"
                            value={typeof (faq as any).answer === "string" ? (faq as any).answer : undefined}
                            fallback={(faq as any).answer}
                            style={{
                              fontFamily: "Roboto, sans-serif",
                              fontWeight: 400,
                              fontSize: 16,
                              lineHeight: "24px",
                              color: "#445162",
                              paddingBottom: 20,
                              whiteSpace: "pre-line",
                            }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
              <div style={{ width: "100%", height: 1, backgroundColor: "#D6D6D6" }} />
            </div>
          </div>

          {/* Hotel / Hotel Apart / B&B Units */}
          <div style={{ display: "flex", flexDirection: "column", gap: 36 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
              <h3
                style={{
                  fontFamily: "Poppins, sans-serif",
                  fontWeight: 600,
                  fontSize: 28,
                  lineHeight: "36px",
                  color: "#01152E",
                  margin: 0,
                }}
              >
                {cmsText?.paris?.hotel?.title || "Hotel / Hotel Apart / B&B Units"}
              </h3>
              <RichText
                value={cmsText?.paris?.hotel?.description}
                style={{
                  fontFamily: "Roboto, sans-serif",
                  fontWeight: 400,
                  fontSize: 16,
                  lineHeight: "24px",
                  color: "#445162",
                  margin: 0,
                  whiteSpace: "pre-line",
                }}
                fallback={"If you own a hotel, aparthotel, or bed & breakfast in the Paris Region and are looking for a trustworthy, experienced, and like-minded management partner, we and our technology may be the right fit for you."}
              />
            </div>
            <div style={{ display: "flex", flexDirection: "column" }}>
              {parisHotelFaqs.map((faq, i) => {
                const faqId = "ph-" + i;
                const isOpen = openFaqs.has(faqId);
                return (
                  <div key={faqId}>
                    <div style={{ width: "100%", height: 1, backgroundColor: "#D6D6D6" }} />
                    <div
                      onClick={() => toggleFaq(faqId)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 21,
                        padding: "20px 0",
                        cursor: "pointer",
                      }}
                    >
                      <span
                        className="flex-1 min-w-0"
                        style={{
                          fontFamily: "Poppins, sans-serif",
                          fontWeight: 500,
                          fontSize: 16,
                          lineHeight: "24px",
                          color: "#01152E",
                          letterSpacing: "0.32px",
                        }}
                      >
                        {faq.question}
                      </span>
                      <img
                        src={isOpen ? accordionOpen : accordionClosed}
                        alt=""
                        style={{
                          width: 28,
                          height: 28,
                          flexShrink: 0,
                          transition: "transform 300ms ease",
                        }}
                      />
                    </div>
                    {faq.answer && (
                      <div
                        style={{
                          display: "grid",
                          gridTemplateRows: isOpen ? "1fr" : "0fr",
                          transition: "grid-template-rows 300ms ease",
                        }}
                      >
                        <div style={{ overflow: "hidden", minHeight: 0 }}>
                          <RichText
                            className="faq-answer"
                            value={typeof faq.answer === "string" ? faq.answer : undefined}
                            fallback={faq.answer}
                            style={{
                              fontFamily: "Roboto, sans-serif",
                              fontWeight: 400,
                              fontSize: 16,
                              lineHeight: "24px",
                              color: "#445162",
                              paddingBottom: 20,
                              whiteSpace: "pre-line",
                            }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
              <div style={{ width: "100%", height: 1, backgroundColor: "#D6D6D6" }} />
            </div>
          </div>
        </div>
      </div>

      {/* Separator */}
      <div
        className="w-full px-4 sm:px-8 lg:px-[120px]"
        style={{ backgroundColor: "#ffffff" }}
      >
        <div style={{ width: "100%", height: 1, backgroundColor: "#445162", opacity: 0.5 }} />
      </div>

      {/* Brussels Region section */}
      <div
        ref={brusselsRef}
        className="w-full flex flex-col xl:flex-row xl:items-start px-4 sm:px-8 lg:px-[120px] py-10 sm:py-16 lg:py-[90px] gap-8 xl:gap-[142px]"
        style={{ backgroundColor: "#ffffff", scrollMarginTop: 156 }}
      >
        {/* Left column */}
        <div className="flex flex-col gap-[30px] w-full xl:basis-[498px] xl:shrink xl:min-w-0">
          <div style={{ display: "flex", flexDirection: "column", gap: 21 }}>
            <img
              src={brusselsIcon}
              alt="Brussels"
              style={{ width: 53, height: 53 }}
            />
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <span
                style={{
                  fontFamily: "Poppins, sans-serif",
                  fontWeight: 600,
                  fontSize: 16,
                  lineHeight: "24px",
                  color: "#B88333",
                  textTransform: "uppercase",
                  letterSpacing: "3.2px",
                }}
              >
                {cmsText?.brussels?.country || "BELGIUM"}
              </span>
              <h2
                className="text-[26px] sm:text-[32px] lg:text-[36px] leading-[34px] sm:leading-[44px] lg:leading-[50px]"
                style={{
                  fontFamily: "Poppins, sans-serif",
                  fontWeight: 600,
                  color: "#01152E",
                  letterSpacing: "0.72px",
                  margin: 0,
                }}
              >
                {cmsText?.brussels?.city || "Brussels"}
              </h2>
            </div>
          </div>
          <RichText
            value={cmsText?.brussels?.intro}
            style={{
              fontFamily: "Roboto, sans-serif",
              fontWeight: 400,
              fontSize: 16,
              lineHeight: "24px",
              color: "#445162",
              margin: 0,
              whiteSpace: "pre-line",
            }}
            fallback={"Belgium is progressively developing its regulatory framework for short- and mid-term rentals to balance housing availability, public safety, and the needs of a dynamic international city.\n\nAt Europea Residences Group, we welcome initiatives that promote simple and clear rules, transparency, and responsible property management. We work closely with owners to ensure their properties comply with the applicable regulations while adapting to the evolving legal framework. We believe that continued dialogue between public authorities and industry professionals will help build a sustainable rental market that benefits residents, owners, and visitors alike."}
          />
        </div>

        {/* Right column - Accordions */}
        <div className="flex flex-col gap-[60px] w-full xl:flex-1 xl:min-w-0">
          {/* Primary Residences */}
          <div style={{ display: "flex", flexDirection: "column", gap: 36 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
              <h3
                style={{
                  fontFamily: "Poppins, sans-serif",
                  fontWeight: 600,
                  fontSize: 28,
                  lineHeight: "36px",
                  color: "#01152E",
                  margin: 0,
                }}
              >
                {cmsText?.brussels?.primary?.title || "Primary Residences"}
              </h3>
              <RichText
                value={cmsText?.brussels?.primary?.description}
                style={{
                  fontFamily: "Roboto, sans-serif",
                  fontWeight: 400,
                  fontSize: 16,
                  lineHeight: "24px",
                  color: "#445162",
                  margin: 0,
                  whiteSpace: "pre-line",
                }}
                fallback={"You own and live in your primary residence and would like to rent it out while you are away. We accept primary residences in Brussels Region and manage them within the applicable legal quota (120 nights per year). We can also assist owners with registration and all administrative formalities required by the relevant authorities. Please note that the Brussels Government is currently working on simplifying the rules and registration process for primary residences. These changes are expected to take effect on January 1, 2027, making it easier for residents to rent out their homes while they are away on holiday."}
              />
            </div>
            <div style={{ display: "flex", flexDirection: "column" }}>
              {brusselsPrimaryFaqs.map((faq, i) => {
                const faqId = "bp-" + i;
                const isOpen = openFaqs.has(faqId);
                return (
                  <div key={faqId}>
                    <div style={{ width: "100%", height: 1, backgroundColor: "#D6D6D6" }} />
                    <div
                      onClick={() => toggleFaq(faqId)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 21,
                        padding: "20px 0",
                        cursor: "pointer",
                      }}
                    >
                      <span
                        className="flex-1 min-w-0"
                        style={{
                          fontFamily: "Poppins, sans-serif",
                          fontWeight: 500,
                          fontSize: 16,
                          lineHeight: "24px",
                          color: "#01152E",
                          letterSpacing: "0.32px",
                        }}
                      >
                        {faq.question}
                      </span>
                      <img
                        src={isOpen ? accordionOpen : accordionClosed}
                        alt=""
                        style={{
                          width: 28,
                          height: 28,
                          flexShrink: 0,
                          transition: "transform 300ms ease",
                        }}
                      />
                    </div>
                    {faq.answer && (
                      <div
                        style={{
                          display: "grid",
                          gridTemplateRows: isOpen ? "1fr" : "0fr",
                          transition: "grid-template-rows 300ms ease",
                        }}
                      >
                        <div style={{ overflow: "hidden", minHeight: 0 }}>
                          <RichText
                            className="faq-answer"
                            value={typeof faq.answer === "string" ? faq.answer : undefined}
                            fallback={faq.answer}
                            style={{
                              fontFamily: "Roboto, sans-serif",
                              fontWeight: 400,
                              fontSize: 16,
                              lineHeight: "24px",
                              color: "#445162",
                              paddingBottom: 20,
                              whiteSpace: "pre-line",
                            }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
              <div style={{ width: "100%", height: 1, backgroundColor: "#D6D6D6" }} />
            </div>
          </div>

          {/* Secondary Residences */}
          <div style={{ display: "flex", flexDirection: "column", gap: 36 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
              <h3
                style={{
                  fontFamily: "Poppins, sans-serif",
                  fontWeight: 600,
                  fontSize: 28,
                  lineHeight: "36px",
                  color: "#01152E",
                  margin: 0,
                }}
              >
                {cmsText?.brussels?.secondary?.title || "Secondary Residences"}
              </h3>
              <RichText
                value={cmsText?.brussels?.secondary?.description}
                style={{
                  fontFamily: "Roboto, sans-serif",
                  fontWeight: 400,
                  fontSize: 16,
                  lineHeight: "24px",
                  color: "#445162",
                  margin: 0,
                  whiteSpace: "pre-line",
                }}
                fallback={"Since January 2025, we accepts new secondary residences only to rent them out exclusively on mid- and long-term rentals. We believe this approach contributes to a healthier balance between long-term housing and alternative accommodation while providing owners with a flexible, sustainable, and fully compliant rental solution.\n\nWe welcome secondary residences for stays of 3 months or longer, allowing owners to generate recurring income while retaining the flexibility to use their property whenever they wish."}
              />
            </div>
            <div style={{ display: "flex", flexDirection: "column" }}>
              {brusselsSecondaryFaqs.map((faq, i) => {
                const faqId = "bs-" + i;
                const isOpen = openFaqs.has(faqId);
                return (
                  <div key={faqId}>
                    <div style={{ width: "100%", height: 1, backgroundColor: "#D6D6D6" }} />
                    <div
                      onClick={() => toggleFaq(faqId)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 21,
                        padding: "20px 0",
                        cursor: "pointer",
                      }}
                    >
                      <span
                        className="flex-1 min-w-0"
                        style={{
                          fontFamily: "Poppins, sans-serif",
                          fontWeight: 500,
                          fontSize: 16,
                          lineHeight: "24px",
                          color: "#01152E",
                          letterSpacing: "0.32px",
                        }}
                      >
                        {faq.question}
                      </span>
                      <img
                        src={isOpen ? accordionOpen : accordionClosed}
                        alt=""
                        style={{
                          width: 28,
                          height: 28,
                          flexShrink: 0,
                          transition: "transform 300ms ease",
                        }}
                      />
                    </div>
                    {(faq as any).answer && (
                      <div
                        style={{
                          display: "grid",
                          gridTemplateRows: isOpen ? "1fr" : "0fr",
                          transition: "grid-template-rows 300ms ease",
                        }}
                      >
                        <div style={{ overflow: "hidden", minHeight: 0 }}>
                          <RichText
                            className="faq-answer"
                            value={typeof (faq as any).answer === "string" ? (faq as any).answer : undefined}
                            fallback={(faq as any).answer}
                            style={{
                              fontFamily: "Roboto, sans-serif",
                              fontWeight: 400,
                              fontSize: 16,
                              lineHeight: "24px",
                              color: "#445162",
                              paddingBottom: 20,
                              whiteSpace: "pre-line",
                            }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
              <div style={{ width: "100%", height: 1, backgroundColor: "#D6D6D6" }} />
            </div>
          </div>

          {/* Hotel / Hotel Apart / Tourist Accommodation / B&B Units */}
          <div style={{ display: "flex", flexDirection: "column", gap: 36 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
              <h3
                style={{
                  fontFamily: "Poppins, sans-serif",
                  fontWeight: 600,
                  fontSize: 28,
                  lineHeight: "36px",
                  color: "#01152E",
                  margin: 0,
                }}
              >
                {cmsText?.brussels?.hotel?.title || "HOTEL / APART HOTEL / RESIDENCE TOURISM"}
              </h3>
              <RichText
                value={cmsText?.brussels?.hotel?.description}
                style={{
                  fontFamily: "Roboto, sans-serif",
                  fontWeight: 400,
                  fontSize: 16,
                  lineHeight: "24px",
                  color: "#445162",
                  margin: 0,
                  whiteSpace: "pre-line",
                }}
                fallback={"For more than 10 years we have designed, launched, and managed hotels, aparthotels, tourist accommodations, and bed & breakfasts throughout the Brussels Region. If you own an operational hospitality business or are considering a new project, we'd be pleased to discuss it."}
              />
            </div>
            <div style={{ display: "flex", flexDirection: "column" }}>
              {brusselsHotelFaqs.map((faq, i) => {
                const faqId = "bh-" + i;
                const isOpen = openFaqs.has(faqId);
                return (
                  <div key={faqId}>
                    <div style={{ width: "100%", height: 1, backgroundColor: "#D6D6D6" }} />
                    <div
                      onClick={() => toggleFaq(faqId)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 21,
                        padding: "20px 0",
                        cursor: "pointer",
                      }}
                    >
                      <span
                        className="flex-1 min-w-0"
                        style={{
                          fontFamily: "Poppins, sans-serif",
                          fontWeight: 500,
                          fontSize: 16,
                          lineHeight: "24px",
                          color: "#01152E",
                          letterSpacing: "0.32px",
                        }}
                      >
                        {faq.question}
                      </span>
                      <img
                        src={isOpen ? accordionOpen : accordionClosed}
                        alt=""
                        style={{
                          width: 28,
                          height: 28,
                          flexShrink: 0,
                          transition: "transform 300ms ease",
                        }}
                      />
                    </div>
                    {(faq as any).answer && (
                      <div
                        style={{
                          display: "grid",
                          gridTemplateRows: isOpen ? "1fr" : "0fr",
                          transition: "grid-template-rows 300ms ease",
                        }}
                      >
                        <div style={{ overflow: "hidden", minHeight: 0 }}>
                          <RichText
                            className="faq-answer"
                            value={typeof (faq as any).answer === "string" ? (faq as any).answer : undefined}
                            fallback={(faq as any).answer}
                            style={{
                              fontFamily: "Roboto, sans-serif",
                              fontWeight: 400,
                              fontSize: 16,
                              lineHeight: "24px",
                              color: "#445162",
                              paddingBottom: 20,
                              whiteSpace: "pre-line",
                            }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
              <div style={{ width: "100%", height: 1, backgroundColor: "#D6D6D6" }} />
            </div>
          </div>
        </div>
      </div>

      {/* Technology section */}
      <div
        ref={technologyRef}
        className="w-full flex flex-col xl:flex-row xl:items-start px-4 sm:px-8 lg:px-[120px] py-10 sm:py-16 lg:py-[90px] gap-8 xl:gap-[142px]"
        style={{ backgroundColor: "#FAF7F1", scrollMarginTop: 156 }}
      >
        <div className="flex flex-col gap-[30px] w-full xl:basis-[498px] xl:shrink xl:min-w-0">
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <span
              style={{
                fontFamily: "Poppins, sans-serif",
                fontWeight: 600,
                fontSize: 16,
                lineHeight: "24px",
                color: "#B88333",
                textTransform: "uppercase",
                letterSpacing: "3.2px",
              }}
            >
              {cmsText?.technology?.label || "Our Technology"}
            </span>
            <h2
              className="text-[26px] sm:text-[32px] lg:text-[36px] leading-[32px] sm:leading-[42px] lg:leading-[46px]"
              style={{
                fontFamily: "Poppins, sans-serif",
                fontWeight: 600,
                color: "#01152E",
                letterSpacing: "0.72px",
                margin: 0,
              }}
            >
              {cmsText?.technology?.heading || "A platform built for every rental model"}
            </h2>
          </div>
          <RichText
            value={cmsText?.technology?.paragraph}
            style={{
              fontFamily: "Roboto, sans-serif",
              fontWeight: 400,
              fontSize: 16,
              lineHeight: "24px",
              color: "#445162",
              margin: 0,
              whiteSpace: "pre-line",
            }}
            fallback={
              <>
                For the past 10 years, we have been developing and continuously
                improving our own{" "}
                <span style={{ fontWeight: 600, color: "#01152E" }}>
                  proprietary technology
                </span>
                , designed specifically for our business — managing, distributing,
                and monitoring everything from traditional residential properties to
                tourism and commercial hospitality units across different regulatory
                environments.
              </>
            }
          />
        </div>

        <div className="flex flex-col gap-[29px] w-full xl:flex-1 xl:min-w-0">
          <span
            style={{
              fontFamily: "Poppins, sans-serif",
              fontWeight: 500,
              fontSize: 18,
              lineHeight: "26px",
              color: "#01152E",
              letterSpacing: "0.36px",
            }}
          >
            {cmsText?.technology?.cards_heading || "One platform, centralizing"}
          </span>
          {/* Single 2-col grid (paired like live: card_1|card_5, card_2|card_6, ...).
              In a grid, the two cards in a row share the row height and every row
              stays aligned — so a taller multi-line label (e.g. FR) never knocks
              the columns out of sync. */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-[12px]">
            {techCardsLeft.flatMap((c, i) => [c, techCardsRight[i]]).map((card) => (
              <div
                key={card.label}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  minHeight: 46,
                  padding: "6px 14px",
                  backgroundColor: "#ffffff",
                  border: "1px solid #D6D6D6",
                  borderRadius: 6,
                  boxShadow: "0px 0px 4px rgba(231, 214, 190, 0.5)",
                }}
              >
                <img
                  src={card.icon}
                  alt=""
                  style={{ width: card.iconW, height: card.iconH, flexShrink: 0 }}
                />
                <span
                  style={{
                    fontFamily: "Poppins, sans-serif",
                    fontWeight: 500,
                    fontSize: 14,
                    lineHeight: "24px",
                    color: "#01152E",
                  }}
                >
                  {card.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Guiding principle quote */}
      <div
        className="w-full flex flex-col items-center px-4 sm:px-8 lg:px-[120px] py-10 sm:py-16 lg:py-[90px] gap-[16px]"
        style={{ backgroundColor: "#01152E" }}
      >
        <span
          className="text-[44px] sm:text-[52px] lg:text-[60px] leading-[36px] sm:leading-[40px] lg:leading-[42px]"
          style={{
            fontFamily: "Poppins, sans-serif",
            fontWeight: 800,
            color: "#B88333",
            textAlign: "center",
          }}
        >
          &ldquo;
        </span>
        <p
          className="text-[20px] sm:text-[24px] lg:text-[30px] leading-[30px] sm:leading-[38px] lg:leading-[46px] max-w-full lg:max-w-[840px]"
          style={{
            fontFamily: "Poppins, sans-serif",
            fontWeight: 400,
            color: "#ffffff",
            textAlign: "center",
            margin: 0,
            whiteSpace: "pre-line",
          }}
        >
          {cmsText?.quote?.text ||
            "We manage properties only when the project fits the legal framework, local community needs, and our operational standards."}
        </p>
        <span
          style={{
            fontFamily: "Poppins, sans-serif",
            fontWeight: 500,
            fontSize: 16,
            lineHeight: "24px",
            color: "#B88333",
            textTransform: "uppercase",
            letterSpacing: "3.2px",
            textAlign: "center",
            marginTop: 34,
          }}
        >
          {cmsText?.quote?.caption || "Our guiding principle"}
        </span>
      </div>

      {/* CTA section */}
      <div
        ref={faqsRef}
        className="w-full flex justify-center px-4 sm:px-8 lg:px-[120px] py-10 sm:py-16 lg:py-[90px]"
        style={{ backgroundColor: "#ffffff", scrollMarginTop: 156 }}
      >
        <div
          className="w-full lg:max-w-[1200px] min-h-[320px] sm:min-h-[360px] lg:h-[380px] px-6 py-10 sm:px-10 lg:px-0 lg:py-0 gap-[32px]"
          style={{
            borderRadius: 16,
            position: "relative",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {/* Teal base */}
          <div
            style={{
              position: "absolute",
              inset: 0,
              backgroundColor: "#01152E",
              borderRadius: 16,
            }}
          />
          {/* Background image — client-editable from the CMS Images tab */}
          <img
            src={cmsImages?.ctaBackground?.url || ctaBg}
            alt=""
            style={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
              opacity: 0.8,
              borderRadius: 16,
            }}
          />
          {/* Dark radial overlay */}
          <div
            style={{
              position: "absolute",
              inset: 0,
              borderRadius: 16,
              opacity: 0.6,
              background:
                "radial-gradient(ellipse at center, rgba(0,2,7,1) 0%, rgba(4,7,15,0.5) 47%, rgba(9,66,84,0) 98%)",
            }}
          />
          {/* Content */}
          <div
            style={{
              position: "relative",
              zIndex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 29,
            }}
          >
            <img
              src={ctaIcon}
              alt=""
              style={{ width: 53, height: 53 }}
            />
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 22,
              }}
            >
              <h3
                className="text-[24px] sm:text-[28px] lg:text-[34px] leading-[30px] sm:leading-[32px] lg:leading-[40px]"
                style={{
                  fontFamily: "Poppins, sans-serif",
                  fontWeight: 600,
                  color: "#ffffff",
                  textAlign: "center",
                  margin: 0,
                }}
              >
                {cmsText?.cta?.title || "Not sure about your property?"}
              </h3>
              <RichText
                className="max-w-full lg:max-w-[428px] cta-rich"
                value={cmsText?.cta?.description}
                style={{
                  fontFamily: "Roboto, sans-serif",
                  fontWeight: 400,
                  fontSize: 16,
                  lineHeight: "24px",
                  color: "#ffffff",
                  textAlign: "center",
                  margin: 0,
                  whiteSpace: "pre-line",
                }}
                fallback="We offer a free legal review to assess the eligibility of your property and the best rental strategy."
              />
            </div>
          </div>
          <button
            onClick={() => setContactOpen(true)}
            style={{
              position: "relative",
              zIndex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: 44,
              padding: "0 24px",
              backgroundColor: "#ffffff",
              borderRadius: 30,
              border: "none",
              cursor: "pointer",
              fontFamily: "Poppins, sans-serif",
              fontWeight: 500,
              fontSize: 14,
              lineHeight: "24px",
              color: "#01152E",
              whiteSpace: "nowrap",
            }}
          >
            {cmsText?.cta?.button || "Request a Property Review"}
          </button>
        </div>
      </div>

      <ContactFormModal
        setSnackbarSeverity={setSnackbarSeverity}
        setSnackbarMessage={setSnackbarMessage}
        setSnackbarOpen={setSnackbarOpen}
        formValues={contactFormValues}
        setFormValues={setContactFormValues}
        isOpen={contactOpen}
        onClose={handleContactClose}
      />

      <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={() => setSnackbarOpen(false)}>
        <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

    </div>
  );
};

export default LegalPageLayout;
